import * as RTooltip from '@radix-ui/react-tooltip'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RTooltip.Provider delayDuration={200} skipDelayDuration={300}>
      {children}
    </RTooltip.Provider>
  )
}

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

/**
 * Supplementary hints only.
 *
 * A tooltip is unreachable on touch and hostile to screen readers when it
 * carries meaning, so anything a respondent or reviewer *must* read goes in a
 * `<Field hint>`, a `<Popover>`, or the WhyDrawer instead. The cost-breakdown
 * tooltip (BB-FR-004) is fine here because the range itself is on the page.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-toast max-w-xs rounded-md border border-border-subtle bg-surface-overlay px-3 py-2',
            'text-caption text-text-primary shadow-lg data-[state=delayed-open]:animate-fade-in',
            className,
          )}
        >
          {content}
          <RTooltip.Arrow className="fill-[--surface-overlay]" />
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  )
}
